import {ContentEditableHtmlEditorPlugin} from '@frontapp/editor-html';

/*
 * Constants.
 */

const existingListDataAttrName = 'data-front-editor-list';
const listNodeNames = new Set(['UL', 'OL']);
const listStyleTypes = ['decimal', 'lower-alpha', 'lower-roman'];

/*
 * Plugin.
 */

/** Adds Front styling to newly added bulleted and numbered lists, without affecting existing lists. */
export function createListStylePlugin(newListClassName: string): ContentEditableHtmlEditorPlugin {
  const selector = [
    `ul.${newListClassName}`,
    `ul[${existingListDataAttrName}]`,
    `ol.${newListClassName}`,
    `ol[${existingListDataAttrName}]`,
  ].join(', ');

  return {
    processTree: (element) => {
      const nodeList = element.querySelectorAll<HTMLUListElement | HTMLOListElement>(selector);

      for (let i = 0; i < nodeList.length; i++) {
        const node = nodeList[i];

        // Replace the class on new lists with a data attribute, so it doesn't get obfuscated on the backend.
        if (node.className === newListClassName) {
          node.removeAttribute('class');
          node.setAttribute(existingListDataAttrName, '');
        }

        updateMargin(node);
        updateListStyleType(element, node);
      }
    },
  };
}

/*
 * Helpers.
 */

/** Resets margins, if there are no existing margin styles. */
function updateMargin(node: HTMLUListElement | HTMLOListElement) {
  if (node.style.marginTop || node.style.marginBottom) {
    return;
  }

  node.style.setProperty('margin-top', '0');
  node.style.setProperty('margin-bottom', '0');
}

function updateListStyleType(container: Node, node: HTMLUListElement | HTMLOListElement) {
  if (node.tagName === 'OL') {
    const depth = findListDepth(container, node);
    const listStyleType = listStyleTypes[depth % listStyleTypes.length];

    // Avoid frivolous DOM mutations.
    if (node.style.listStyleType === listStyleType) {
      return;
    }

    node.style.setProperty('list-style-type', listStyleType);
    return;
  }

  if (node.tagName === 'UL') {
    // This plugin does not override the style of unordered lists so just remove any left-over styling.
    node.style.removeProperty('list-style-type');
  }
}

function findListDepth(container: Node, node: HTMLUListElement | HTMLOListElement) {
  let depth = 0;

  let ancestorNode: Node | null = node.parentNode;
  while (ancestorNode && ancestorNode !== container) {
    if (listNodeNames.has(ancestorNode.nodeName)) {
      depth += 1;
    }

    ancestorNode = ancestorNode.parentNode;
  }

  return depth;
}
